import React from "react";

import { FormControl, FormHelperText } from "@mui/material";
import withStyles from "@mui/styles/withStyles";
import { InfoOutlined as InfoOutlinedIcon } from "@mui/icons-material";
import classnames from "classnames";
import "react-phone-number-input/style.css";
import PhoneInputComponent, { DefaultInputComponentProps } from "react-phone-number-input";
import Input from "react-phone-number-input/input";

import { Error } from "../../../models/error";
import { phoneNumberIsValid } from "../../../util/formatPhoneNumber";
import TextInput from "../Input";
import useStyles from "./styles";
import { colors } from "@zapalacorp/tokens";

interface Props extends DefaultInputComponentProps {
  label?: string;
  error?: boolean;
  errorMessage?: string;
  errorResponse?: Error;
  minLength?: number;
  required?: boolean;
  className?: string;
  isLabelStyle?: boolean;
  "data-cy"?: string;
  labelStyle?: string;
  withoutCountryFlag?: boolean;
  withoutFormat?: boolean;
  defaultCountry?: any;
  onChange?: (value: string) => void;
}

const FormControlCustomized = withStyles((_props) => ({
  root: {
    "& .react-tel-input": {
      fontFamily: "Manrope",
    },
    "& ::placeholder": {
      color: "#C6CAD1",
    },
    "& ::-ms-input-placeholder": {
      color: "#C6CAD1",
    },
    "& input:-webkit-autofill, input:-webkit-autofill:hover, input:-webkit-autofill:focus, input:-webkit-autofill:active":
      {
        "-webkit-box-shadow": "0 0 0 30px white inset !important",
        "-webkit-text-fill-color": "#667085",
      },
    display: "flex",
    justifyContent: "flex-end",
    "&:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: (props: { bordercolor?: string; disabled?: boolean }) =>
        !props?.disabled ? props?.bordercolor : "#C4C4C4",
    },
    "&:active .MuiOutlinedInput-notchedOutline": {
      borderColor: (props: { bordercolor?: string; disabled?: boolean }) =>
        !props?.disabled ? props?.bordercolor : "#C4C4C4",
    },
    "& input:disabled": {
      backgroundColor: (props: { hiddenLabel?: boolean; disabled?: boolean }) =>
        props.hiddenLabel && props?.disabled ? "transparent" : "rgb(249, 250, 251)",
    },
    "& .PhoneInput--disabled": {
      backgroundColor: (props: { hiddenLabel?: boolean; disabled?: boolean }) =>
        props.hiddenLabel && props?.disabled ? "transparent" : "rgb(249, 250, 251)",
    },
  },
}))(FormControl);

const PhoneInput = (props: Props) => {
  const {
    label,
    errorMessage,
    error,
    errorResponse,
    minLength,
    isLabelStyle,
    required,
    placeholder = "",
    disabled,
    onChange,
    labelStyle = "",
    withoutCountryFlag = false,
    withoutFormat = false,
    defaultCountry = undefined,
    ...rest
  } = props;

  const classes = useStyles({
    hoverBorderColor: error ? "#D92D20" : colors.brandBlue500,
    disabled,
  });

  const renderLabel = () => {
    if (label) {
      return (
        <span className={classnames(classes.label, labelStyle)}>
          {label}
          {renderRequiredLabel()}
        </span>
      );
    }

    return null;
  };

  const renderRequiredLabel = () => {
    if (required) {
      return <span className={classes.required}>{"*"}</span>;
    }

    return null;
  };

  const renderError = () => {
    if (error && errorMessage) {
      return (
        <FormHelperText className={classes.formHelper} data-cy="errors">
          <InfoOutlinedIcon htmlColor="#D92D20" className={classes.iconError} />
          {errorMessage}
        </FormHelperText>
      );
    }
    if (errorResponse && errorResponse.field === rest.name) {
      return (
        <FormHelperText className={classes.formHelper} data-cy="errors">
          <InfoOutlinedIcon htmlColor="#D92D20" className={classes.iconError} />
          {errorResponse?.message}
        </FormHelperText>
      );
    }
  };

  const renderPhoneInput = (classes: string) => {
    if (withoutCountryFlag) {
      return (
        <Input
          placeholder={placeholder}
          className={classes}
          disabled={disabled}
          onChange={(event) => onChange && onChange(event as string)}
          {...rest}
        />
      );
    }

    return (
      <PhoneInputComponent
        defaultCountry={defaultCountry}
        placeholder={placeholder}
        className={classes}
        disabled={disabled}
        onChange={(event) => onChange && onChange(event as string)}
        {...rest}
      />
    );
  };

  const renderInput = () => {
    if (error || (errorResponse && errorResponse?.field === rest.name)) {
      return renderPhoneInput(classnames(classes.input, classes.borderError));
    }

    if (disabled) {
      return renderPhoneInput(classnames(classes.input, classes.borderDisabled));
    }

    return renderPhoneInput(classnames(classes.input, classes.borderDefault));
  };

  return withoutFormat ? (
    <TextInput
      placeholder={placeholder}
      disabled={disabled}
      onChange={(event) => {
        const { value } = event.target;

        if (phoneNumberIsValid(value) && onChange) {
          onChange(value);
        }
      }}
      required={required}
      label={label}
      errorMessage={errorMessage}
      error={error}
      errorResponse={errorResponse}
      hiddenLabel={isLabelStyle}
      {...rest}
    />
  ) : (
    <FormControlCustomized
      disabled={disabled}
      bordercolor={
        error || (errorResponse && errorResponse?.field === rest.name)
          ? "#D92D20"
          : colors.brandBlue500
      }
      hiddenLabel={isLabelStyle}
      data-cy="phone-form-control"
    >
      {renderLabel()}
      {renderInput()}
      {renderError()}
    </FormControlCustomized>
  );
};

export default PhoneInput;
